import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AuthService} from '../core/services/auth.service';
import {createSkipDelegationContext, SKIP_DELEGATION_CONTEXT} from '../core/http/skip-delegation-context.token';
import {createSkipGlobalLoaderContext, SKIP_GLOBAL_LOADER} from '../core/http/skip-global-loader.token';
import {SKIP_SESSION_REFRESH} from '../core/http/skip-session-refresh.token';

export interface StreamStatus {
  broadcasterId: string;
  live: boolean;
  title?: string;
  gameName?: string;
  startedAt?: string;
  viewerCount?: number | null;
  updatedAt: string;
}

export interface MicrosoftSettings {
	configured: boolean;
	linked: boolean;
	minecraftName?: string;
	minecraftId?: string;
	linkedAt?: string;
}

export interface MinecraftSettings {
	configured: boolean;
	sourceId?: string;
	playerName?: string;
	heartbeatSeconds: number;
	enabled: boolean;
	apiToken?: string;
}

export interface MinecraftWorldStatus {
	worldId: string | null;
	name: string;
	updatedAt: string;
	online: boolean;
	hardcore: boolean;
	day: number;
	playtime: string;
}

export interface StreamTitlePreview { preview: string; ready: boolean; reason?: string | null; }
export interface StreamTitleStatus extends StreamTitlePreview {
    template: string;
    automatic: boolean;
    currentTitle: string;
    live: boolean;
    linked: boolean;
    canManage: boolean;
    lastAppliedAt?: string | null;
    lastError?: string | null;
}

export interface MinecraftStatus {
	worldId?: string | null;
	worlds?: MinecraftWorldStatus[];
	online: boolean;
	sourceId?: string;
	updatedAt?: string;
	variables?: Record<string, string>;
}

export interface CommandUsageStats {
  command: string;
  usageCount: number;
}

export interface CommandStats {
  totalExecutions: number;
  topCommands: CommandUsageStats[];
}

export interface ChatAnalyticsCluster {
  representativeMessage: string;
  displayMessage?: string | null;
  normalizedMessage: string;
  canonicalMessage: string;
  topic?: string | null;
  intent?: string | null;
  occurrenceCount: number;
  uniqueUsers: number;
  question: boolean;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
}

export interface ChatAnalyticsMetric {
  key: string;
  occurrenceCount: number;
  uniqueUsers: number;
}

export interface ChatAnalyticsTimelineBucket {
  bucketStart: string;
  bucketEnd?: string | null;
  totalMessages: number;
  questionMessages: number;
  uniqueUsers: number;
}

export interface ChatAnalyticsHotMoment {
  bucketStart: string;
  bucketEnd?: string | null;
  totalMessages: number;
  questionMessages: number;
  uniqueUsers: number;
}

export interface ChatAnalyticsCorrelatedMoment {
  bucketStart: string;
  bucketEnd?: string | null;
  totalMessages: number;
  questionMessages: number;
  uniqueUsers: number;
  averageViewers: number;
  peakViewers: number;
  raidCount: number;
  topRaiderLogin?: string | null;
  topRaidViewers: number;
  eventCount: number;
  topEventType?: string | null;
  topEventLabel?: string | null;
  commandCount: number;
  topCommand?: string | null;
}

export interface ChatAnalyticsHighlightCandidate {
  bucketStart: string;
  bucketEnd?: string | null;
  title: string;
  summary: string;
  score: number;
}

export interface ViewerTimelineBucket {
  bucketStart: string;
  bucketEnd?: string | null;
  averageViewers: number;
  peakViewers: number;
  sampleCount: number;
}

export interface ChatAnalyticsFaqCandidate {
  question: string;
  displayQuestion?: string | null;
  category: string;
  topic?: string | null;
  intent?: string | null;
  occurrenceCount: number;
  uniqueUsers: number;
  confidence: number;
  suggestedAnswer: string;
}

export interface ChatAnalyticsRecommendation {
  type: string;
  title: string;
  description: string;
  sourceQuestion: string;
  displayQuestion?: string | null;
  category: string;
  confidence: number;
}

export interface ChatAnalyticsRecurringQuestion {
  question: string;
  displayQuestion?: string | null;
  category: string;
  topic?: string | null;
  intent?: string | null;
  occurrenceCount: number;
  uniqueUsers: number;
  streamCount: number;
  confidence: number;
  suggestedAnswer: string;
}

export interface StreamChatFaqEntry {
  id: string;
  question: string;
  normalizedQuestion: string;
  category: string;
  answer: string;
  enabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface StreamChatAnalytics {
  streamSessionId: string;
  broadcasterId: string;
  startedAt?: string | null;
  endedAt?: string | null;
  title?: string | null;
  gameName?: string | null;
  totalMessages: number;
  uniqueUsers: number;
  questionMessages: number;
  lastMessageAt?: string | null;
  analyzedAt?: string | null;
  topQuestions: ChatAnalyticsCluster[];
  actionableQuestions: ChatAnalyticsFaqCandidate[];
  faqCandidates: ChatAnalyticsFaqCandidate[];
  recommendations: ChatAnalyticsRecommendation[];
  recurringQuestions: ChatAnalyticsRecurringQuestion[];
  viewerTimeline: ViewerTimelineBucket[];
  topMessages: ChatAnalyticsCluster[];
  topTopics: ChatAnalyticsMetric[];
  topIntents: ChatAnalyticsMetric[];
  timeline: ChatAnalyticsTimelineBucket[];
  hotMoments: ChatAnalyticsHotMoment[];
  correlatedMoments: ChatAnalyticsCorrelatedMoment[];
  highlightCandidates: ChatAnalyticsHighlightCandidate[];
  timelineBucketSeconds: number;
  timelineBucketMinutes: number;
}

export interface TwitchFollower {
  id: string;
  broadcasterId: string;
  followerUserId: string;
  followerLogin: string;
  followerDisplayName?: string | null;
  profileImageUrl?: string | null;
  firstFollowedAt: string;
  lastFollowedAt: string;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
}

export interface TwitchFollowerPage {
  followers: TwitchFollower[];
  total: number;
  page: number;
  size: number;
  hasNext: boolean;
}

export interface TwitchFollowerSyncResponse {
  synced: boolean;
  fetched: number;
  updated: number;
  deactivated: number;
  syncedAt: string;
}

export interface TwitchFollowerHygieneItem {
  follower: TwitchFollower;
  score: number;
  riskLevel: string;
  reasons: string[];
  status: string;
  note?: string | null;
  lastAnalyzedAt?: string | null;
  twitchBanned: boolean;
  twitchBanExpiresAt?: string | null;
  twitchBanCheckedAt?: string | null;
  decidedAt?: string | null;
}

export interface TwitchFollowerHygieneSummary {
  high: number;
  medium: number;
  low: number;
  banned: number;
  trusted: number;
  ignored: number;
  review: number;
}

export interface TwitchFollowerHygieneActionAvailability {
  action: string;
  available: boolean;
  requiredScope?: string | null;
  reason?: string | null;
}

export interface TwitchFollowerHygienePage {
  followers: TwitchFollowerHygieneItem[];
  summary: TwitchFollowerHygieneSummary;
  availableActions: TwitchFollowerHygieneActionAvailability[];
  total: number;
  page: number;
  size: number;
  hasNext: boolean;
}

export interface TwitchFollowerHygieneDecisionResponse {
  followerUserId: string;
  status: string;
  note?: string | null;
  decidedAt?: string | null;
}

export interface TwitchFollowerHygieneActionResponse {
  followerUserId: string;
  action: string;
  status: string;
  twitchStatus?: string | null;
  errorMessage?: string | null;
  createdAt?: string | null;
}

export interface TwitchFollowerHygieneScanResponse {
  scanned: number;
  high: number;
  medium: number;
  low: number;
  banned: number;
  scannedAt: string;
}

export interface TwitchFollowerHygieneBulkActionResponse {
  requested: number;
  applied: number;
  failed: number;
  results: TwitchFollowerHygieneActionResponse[];
}

export interface StreamSessionSummary {
  streamSessionId: string;
  broadcasterId: string;
  startedAt?: string | null;
  endedAt?: string | null;
  title?: string | null;
  gameName?: string | null;
}

export interface StreamChatAnalyticsRefreshResponse {
  refreshedSessions: number;
  streamSessionIds: string[];
}

export interface AdminSiteAnalyticsDailyPoint {
  date: string;
  bucketStart?: string | null;
  bucketEnd?: string | null;
  pageViews: number;
  uniqueVisitors: number;
  actionCount: number;
  averageTimeSeconds: number;
}

export interface AdminSiteAnalyticsPageMetric {
  pageKey: string;
  pageViews: number;
  uniqueVisitors: number;
  averageTimeSeconds: number;
}

export interface AdminSiteAnalyticsActionMetric {
  pageKey: string;
  actionKey: string;
  eventCount: number;
  uniqueVisitors: number;
}

export interface AdminSiteAnalyticsTransitionMetric {
  fromPageKey: string;
  toPageKey: string;
  transitionCount: number;
  uniqueVisitors: number;
}

export interface AdminSiteAnalyticsOverview {
  fromDate: string;
  toDate: string;
  totalPageViews: number;
  totalUniqueVisitors: number;
  totalActionCount: number;
  averageTimeSeconds: number;
  dailyTimeline: AdminSiteAnalyticsDailyPoint[];
  topPages: AdminSiteAnalyticsPageMetric[];
  topActions: AdminSiteAnalyticsActionMetric[];
  topTransitions: AdminSiteAnalyticsTransitionMetric[];
}

export interface User {
  id: string;
  username: string;
  email: string;
  emailVerified?: boolean;
  profileImageUrl?: string;
  createdAt: string;
  roles?: string[];
}

export interface AuthResponse {
  url: string;
}

export interface FeatureFlag {
  key: string;
  status: 'DISABLED' | 'ENABLED' | 'COMING_SOON';
  defaultStatus: 'DISABLED' | 'ENABLED' | 'COMING_SOON';
  visible?: boolean | null;
  accessible?: boolean | null;
  updatedAt?: string | null;
  updatedByUserId?: string | null;
}

export interface SiteAccessStatus {
  comingSoon: boolean;
  accessible: boolean;
  admin: boolean;
}

export interface TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn?: number;
}

export interface AccountSecurityStatus {
  email?: string | null;
  emailVerified: boolean;
  localPasswordConfigured: boolean;
  recoveryReady: boolean;
  verificationPending: boolean;
}

export interface CommandDefinition {
  id: string;
  userId?: string;
  name: string;
  platform: string;
  responseTemplate: string;
  cooldownSeconds: number;
  enabled: boolean;
  /** always | live_only | offline_only — par défaut toujours disponible */
  availableWhen?: string;
  /** Rôles autorisés (everyone, moderator, vip, subscriber, follower), séparés par des virgules */
  allowedRoles?: string;
  /** Rôles exemptés du cooldown (moderator, vip, subscriber, follower), virgules. Vide = cooldown pour tous. */
  cooldownExemptRoles?: string;
  /** Mots-clés ou phrases (séparés par des virgules) qui déclenchent la commande si le message les contient. Vide = uniquement !nom. */
  keywords?: string;
  /** Alias de commande (sans !, séparés par des virgules). Exemple: musique,musics */
  aliases?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Paramètre utilisateur pour une commande intégrée (!cmd, !uptime, !ls, !myuptime, !queue, !add). */
export interface DefaultCommandSetting {
  id?: string;
  commandKey: string;
  enabled: boolean;
  responseTemplate: string | null;
}

export interface CommandVariableCatalogItem {
  key: string;
  platform: string;
  descriptionKey: string;
  comingSoon: boolean;
}

export interface TwitchIrcRegisterResponse {
  status: string;
  botLogin?: string;
  moderatorManaged?: boolean;
  moderatorStatus?: string;
  moderatorErrorCode?: number;
}

/** Valeurs possibles pour availableWhen */
export const COMMAND_AVAILABLE_WHEN = ['always', 'live_only', 'offline_only'] as const;
/** Rôles Twitch pour restrictions de commandes (broadcaster = streamer, editor = éditeurs chaîne, artist = badge contributeur art) */
export const COMMAND_ROLES = ['everyone', 'broadcaster', 'moderator', 'editor', 'vip', 'subscriber', 'follower', 'artist'] as const;

export interface SpotifyLinkStatus {
  spotifyUserId: string | null;
  linked: boolean;
}

export interface SpotifyAppSettings {
  clientId: string;
  clientSecretConfigured: boolean;
  configured: boolean;
  callbackUri: string;
}

export interface SpotifyProfile {
  id: string;
  displayName: string;
  imageUrl: string | null;
}

export interface SpotifyNowPlaying {
  trackName: string;
  artistName: string;
  albumName?: string;
  albumImageUrl?: string | null;
  playlistName?: string;
  trackUrl?: string | null;
  playlistUrl?: string | null;
  progressMs?: number | null;
  durationMs?: number | null;
  progressPercent?: number | null;
  playing: boolean;
  recentTracks?: SpotifyRecentTrack[];
}

export interface SpotifyRecentTrack {
  trackName: string;
  artistName: string;
  albumName?: string | null;
  albumImageUrl?: string | null;
  trackUrl?: string | null;
  playedAt?: string | null;
}

export interface DiscordLinkStatus {
  discordUserId: string | null;
  linked: boolean;
}

export interface NightbotLinkStatus {
  nightbotUserId: string | null;
  linked: boolean;
}

export interface TwitchScopeStatus {
  linked: boolean;
  scopeChangeRequired: boolean;
  scopes: string[];
  requiredScopes: string[];
  missingScopes: string[];
  deprecatedScopes: string[];
}

export interface NightbotImportData {
  commands: Array<{ command: string; message: string; cooldown?: number | null; enabled?: boolean }>;
  timers: Array<{ name: string; message: string; interval?: number | null; enabled?: boolean }>;
}

export interface StreamElementsImportData {
  channelId?: string | null;
  channelName?: string | null;
  commands: Array<{
    command: string;
    message: string;
    cooldown?: number | null;
    enabled?: boolean;
    aliases?: string[] | null;
    keywords?: string[] | null;
  }>;
  timers: Array<{
    name: string;
    message: string;
    intervalSeconds?: number | null;
    enabled?: boolean;
    onlyWhenLive?: boolean;
  }>;
}

export interface DiscordProfile {
  id: string;
  username: string;
  avatarUrl?: string | null;
  email?: string | null;
  createdAt?: string | null;
}

export interface DiscordGuild {
  id: string;
  name: string;
  iconUrl?: string | null;
  memberCount?: number | null;
  presenceCount?: number | null;
}

export interface DiscordStats {
  connectedCount?: number | null;
  memberCount?: number | null;
}

export interface DiscordLinkedServer {
  id: string;
  guildId: string;
  guildName?: string | null;
  guildIconUrl?: string | null;
  memberCount?: number | null;
  presenceCount?: number | null;
  primary: boolean;
  createEventOnGoLive: boolean;
}

export interface Delegation {
  id: string;
  ownerUserId: string;
  ownerUsername?: string;
  managerUserId: string;
  managerUsername?: string;
  permissions: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DelegationInvitation {
  id: string;
  ownerUserId: string;
  ownerUsername?: string;
  inviteeUserId: string;
  permissions: string[];
  status: string;
  expiresAt?: string;
  createdAt?: string;
}

export interface SupportExchangeResponse {
  supportSessionId: string;
  actorUserId: string;
  ownerUserId: string;
  ownerUsername?: string | null;
  permissions: string[];
  reason?: string | null;
  expiresAt?: string | null;
  expiresAtEpochMs?: number | null;
}

export interface SupportNotice {
  id: string;
  actorUserId: string;
  actorUsername?: string | null;
  targetUserId: string;
  targetUsername?: string | null;
  reason: string;
  status: string;
  createdAt?: string | null;
  lastExchangedAt?: string | null;
  closedAt?: string | null;
}

/** Canal Discord (liste pour choix publication planning). */
export interface DiscordChannelInfo {
  id: string;
  name: string;
  type: number;
  parentId?: string | null;
}

/** Config publication du planning Twitch sur un canal Discord. */
export interface ScheduleDiscordConfig {
  id?: string;
  userId?: string;
  guildId: string;
  enabled?: boolean;
  channelId: string;
  messageContent?: string | null;
  embedTemplate?: string | null;
  visualTemplate?: string | null;
  visualTemplateType?: string | null;
  visualLayout?: string | null;
  imageSpoiler?: boolean;
  autoPostEnabled: boolean;
  autoPostDayOfWeek?: number | null;
  autoPostTime?: string | null;
  lastPostedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DiscordLiveAnnouncementConfig {
  id?: string;
  userId?: string;
  guildId: string;
  enabled?: boolean;
  channelId: string;
  messageContent?: string | null;
  lastPostedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DiscordClipAnnouncementConfig {
  id?: string;
  userId?: string;
  guildId: string;
  enabled?: boolean;
  channelId: string;
  messageContent?: string | null;
  lastPostedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AutoMessage {
  id: string;
  label: string;
  message: string;
  intervalSeconds: number;
  triggerType?: 'INTERVAL' | 'EVENT';
	eventType?: 'FOLLOW' | 'SUBSCRIPTION' | 'SUBSCRIPTION_GIFT' | 'SUBSCRIPTION_MESSAGE' | 'CHEER' | 'RAID' | 'CHANNEL_POINTS' | 'minecraft.day.changed' | 'minecraft.player.death' | 'minecraft.hardcore.ended';
  eventDispatchMode?: 'IMMEDIATE' | 'INTERVAL';
  eventSummaryIntervalSeconds?: number;
  onlyWhenLive: boolean;
  enabled: boolean;
  lastSentAt?: string;
  asAnnouncement?: boolean;
}

export interface TwitchModerationSettings {
  id?: string;
  userId?: string;
  enabled: boolean;
  exemptRoles: string;
  suspiciousAccountsEnabled: boolean;
  suspiciousAccountMaxAgeDays: number;
  suspiciousAccountMinLoginDigits: number;
  suspiciousAccountRequireNotFollower: boolean;
  suspiciousAccountAction: string;
  suspiciousAccountTimeoutSeconds: number;
  followWaveEnabled: boolean;
  followWaveWindowSeconds: number;
  followWaveMaxSuspiciousAccounts: number;
  followWaveAction: string;
  followWaveTimeoutSeconds: number;
  raidProtectionEnabled: boolean;
  raidProtectionWindowSeconds: number;
  raidProtectionMaxRaids: number;
  raidProtectionMaxTotalViewers: number;
  raidProtectionDetectKnownBots: boolean;
  raidProtectionAction: string;
  capsEnabled: boolean;
  capsMinLetters: number;
  capsRatioPercent: number;
  capsAction: string;
  capsTimeoutSeconds: number;
  emoteSpamEnabled: boolean;
  emoteSpamMaxEmotes: number;
  emoteSpamMaxEmoteRatioPercent: number;
  emoteSpamAction: string;
  emoteSpamTimeoutSeconds: number;
  blockedTermsEnabled: boolean;
  blockedTermsPresetCategories: string;
  blockedTerms: string;
  blockedTermsAction: string;
  blockedTermsTimeoutSeconds: number;
}

export interface LevelSettings {
	id?: string;
	userId?: string;
	enabled: boolean;
	rankCommandEnabled: boolean;
	leaderboardCommandEnabled: boolean;
	levelUpAnnouncementEnabled: boolean;
	levelUpAnnouncementTemplate: string;
	levelUpAnnouncementMode: 'IMMEDIATE' | 'INTERVAL';
	levelUpAnnouncementIntervalSeconds: number;
	xpPerMessageMin: number;
	xpPerMessageMax: number;
	messageCooldownSeconds: number;
	watchXpPer5Minutes: number;
	messageXpPerMinute: number;
	primeSubXp: number;
	tier1SubXp: number;
	tier2SubXp: number;
	tier3SubXp: number;
	bitsXpPer100: number;
	levelResetAt?: string | null;
	minimumMessageLength: number;
	ignoreCommands: boolean;
	ignoreBotMessages: boolean;
	ignoreModeratorMessages: boolean;
	excludedRoles: string;
	liveBonusPercent: number;
	subscriberBonusPercent: number;
	vipBonusPercent: number;
	levelFormulaBaseXp: number;
	levelFormulaMultiplier: number;
	maxLevel: number;
	rankTiers: LevelRankTier[];
	panelEnabled: boolean;
	panelTitle: string;
	panelDescription: string;
	panelShowLeaderboard: boolean;
	panelLeaderboardSize: number;
	panelShowLevelsOffline: boolean;
	panelShowMusic: boolean;
	panelShowMusicOffline: boolean;
	panelShowCommands: boolean;
	panelShowCommandsOffline: boolean;
}

export interface LevelRankTier {
	name: string;
	minLevel: number;
	maxLevel: number;
}

export interface LevelPanelEntry {
	position: number;
	login: string;
	totalXp: number;
	level: number;
	rankName?: string | null;
	currentLevelXp: number;
	nextLevelXp: number;
	progressPercent: number;
	awardedMessageCount: number;
	lastAwardedAt?: string | null;
}

export interface LevelPanelPreview {
	enabled: boolean;
	panelEnabled: boolean;
	period?: 'week' | 'month' | 'global' | string | null;
	title?: string | null;
	description?: string | null;
	showLeaderboard?: boolean | null;
	leaderboardSize?: number | null;
	participantCount?: number | null;
	lastUpdatedAt?: string | null;
	entries: LevelPanelEntry[];
}

export interface LevelResetResponse {
	reset: boolean;
}

export interface PublicStreamerPanel {
	streamer: {
		userId: string;
		username: string;
		displayName: string;
		profileImageUrl?: string | null;
		twitchUserId?: string | null;
	};
	nowPlaying?: SpotifyNowPlaying | null;
	levels: LevelPanelPreview;
	streamLive?: boolean;
	visibility?: PublicPanelVisibility | null;
	commands: PublicPanelCommand[];
}

export interface PublicPanelVisibility {
	levels: boolean;
	music: boolean;
	commands: boolean;
}

export interface PublicPanelCommand {
	name: string;
	type: 'builtin' | 'custom' | string;
	platform?: string | null;
	description?: string | null;
	cooldownSeconds?: number | null;
	availableWhen?: string | null;
	allowedRoles?: string | null;
	aliases?: string | null;
}

export interface TwitchModerationAnalyticsDailyPoint {
  date: string;
  bucketStart?: string | null;
  bucketEnd?: string | null;
  totalActions: number;
  successfulActions: number;
  failedActions: number;
}

export interface TwitchModerationAnalyticsActionMetric {
  actionType: string;
  totalActions: number;
  successfulActions: number;
  failedActions: number;
}

export interface TwitchModerationAnalyticsStreamerMetric {
  broadcasterUserId: string;
  channelLogin: string;
  totalActions: number;
  successfulActions: number;
  failedActions: number;
}

export interface TwitchModerationBotProtectionSummary {
  followBotActions: number;
  followBotsStopped: number;
  raidBotActions: number;
  raidBotsStopped: number;
}

export interface TwitchModerationRecentBotAction {
  broadcasterUserId: string;
  channelLogin: string;
  targetLogin: string;
  ruleKey: string;
  actionType: string;
  success: boolean;
  date: string;
  occurredAt: string;
}

export interface TwitchModerationAnalyticsOverview {
  fromDate: string;
  toDate: string;
  totalActions: number;
  successfulActions: number;
  failedActions: number;
  botProtectionSummary: TwitchModerationBotProtectionSummary;
  dailyTimeline: TwitchModerationAnalyticsDailyPoint[];
  topActions: TwitchModerationAnalyticsActionMetric[];
  topStreamers: TwitchModerationAnalyticsStreamerMetric[];
  recentBotActions: TwitchModerationRecentBotAction[];
}

export interface TopRaider {
  login: string;
  raidCount: number;
  totalViewers: number;
  peakViewers: number;
  period?: 'week' | 'month' | 'global';
}

export interface TopViewer {
  login: string;
  watchSeconds: number;
  watchHours: number;
  streamCount: number;
  period: 'week' | 'month' | 'global';
}

/** Utilisateur avec un rôle sur la chaîne (modérateur, VIP) — format Helix, enrichi avec profile_image_url. */
export interface TwitchChannelRoleUser {
  user_id: string;
  user_login: string;
  user_name: string;
  profile_image_url?: string;
}

export interface TwitchCategory {
  id: string;
  name: string;
  box_art_url?: string;
}

interface HttpRequestContextOptions {
  skipGlobalLoader?: boolean;
  skipDelegation?: boolean;
  skipSessionRefresh?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
	get minecraftEndpoint(): string {
		return new URL(this.apiUrl.replace(/\/$/, '') + '/minecraft/snapshots', window.location.origin).href;
	}

	getMicrosoftSettings(): Observable<MicrosoftSettings> {
		return this.http.get<MicrosoftSettings>(this.getSecurityBase() + '/me/microsoft/settings', {withCredentials: true});
	}

	connectMicrosoft(): Observable<{ authorizationUrl: string }> {
		return this.http.post<{
			authorizationUrl: string
		}>(this.getSecurityBase() + '/me/microsoft/connect', {}, {withCredentials: true});
	}

	unlinkMicrosoft(): Observable<void> {
		return this.http.post<void>(this.getSecurityBase() + '/me/microsoft/unlink', {}, {withCredentials: true});
	}

	getMinecraftSettings(): Observable<MinecraftSettings> {
		return this.http.get<MinecraftSettings>(this.apiUrl + '/minecraft/settings', {withCredentials: true});
	}

	saveMinecraftSettings(body: {
		sourceId: string;
		playerName: string;
		heartbeatSeconds: number;
		enabled: boolean
	}): Observable<MinecraftSettings> {
		return this.http.put<MinecraftSettings>(this.apiUrl + '/minecraft/settings', body, {withCredentials: true});
	}

	rotateMinecraftToken(): Observable<MinecraftSettings> {
		return this.http.post<MinecraftSettings>(this.apiUrl + '/minecraft/token', {}, {withCredentials: true});
	}

	getMinecraftStatus(): Observable<MinecraftStatus> {
		return this.http.get<MinecraftStatus>(this.apiUrl + '/minecraft/status', {
			withCredentials: true,
			context: createSkipGlobalLoaderContext()
		});
	}
  private readonly apiUrl = environment.apiUrl;
  private readonly securityApiUrl = environment.securityApiUrl;
  private readonly botApiUrl = environment.botApiUrl;
  private readonly batchApiUrl = (environment as { batchApiUrl?: string }).batchApiUrl ?? '';

  constructor(
    private http: HttpClient,
    private auth: AuthService
  ) {}

  /** Ne pas exécuter les appels qui nécessitent le token si non connecté. */
  private requireAuth<T>(): Observable<T> | null {
    if (!this.auth.isLoggedIn) {
      return throwError(() => new Error('Non connecté'));
    }
    return null;
  }

  /** Appel direct au service Security pour l’auth Twitch */
  getTwitchLoginUrl(): Observable<AuthResponse> {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return this.http.get<AuthResponse>(`${base}/auth/twitch/login`, { withCredentials: true });
  }

  handleTwitchCallback(code: string, state: string): Observable<TokenResponse> {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return this.http.post<TokenResponse>(`${base}/auth/twitch/callback`, { code, state }, { withCredentials: true });
  }

  localLogin(email: string, password: string): Observable<TokenResponse> {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return this.http.post<TokenResponse>(`${base}/auth/local/login`, { email, password }, { withCredentials: true });
  }

  /** Base URL du service Security (me, auth) */
  private getSecurityBase(): string {
    return this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
  }

  /** URL de connexion Spotify (lier le compte) — requiert auth */
  getSpotifyLoginUrl(): Observable<AuthResponse> {
    const block = this.requireAuth<AuthResponse>();
    if (block) return block;
    return this.http.get<AuthResponse>(`${this.getSecurityBase()}/auth/spotify/login`, { withCredentials: true });
  }

  /** Statut de liaison Spotify (GET /me/spotify) */
  getSpotifyStatus(): Observable<SpotifyLinkStatus> {
    const block = this.requireAuth<SpotifyLinkStatus>();
    if (block) return block;
    return this.http.get<SpotifyLinkStatus>(`${this.getSecurityBase()}/me/spotify`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /** Délier le compte Spotify */
  getSpotifyAppSettings(): Observable<SpotifyAppSettings> {
    const block = this.requireAuth<SpotifyAppSettings>();
    if (block) return block;
    return this.http.get<SpotifyAppSettings>(`${this.getSecurityBase()}/me/spotify/app-settings`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  saveSpotifyAppSettings(payload: { clientId: string; clientSecret: string }): Observable<SpotifyAppSettings> {
    const block = this.requireAuth<SpotifyAppSettings>();
    if (block) return block;
    return this.http.put<SpotifyAppSettings>(`${this.getSecurityBase()}/me/spotify/app-settings`, payload, {
      withCredentials: true
    });
  }

  deleteSpotifyAppSettings(): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/spotify/app-settings`, {
      withCredentials: true
    });
  }

  unlinkSpotify(): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/auth/spotify/unlink`, null, { withCredentials: true });
  }

  /** Profil Spotify connecté (nom, image) pour la page d'intégration */
  getSpotifyProfile(): Observable<SpotifyProfile> {
    const block = this.requireAuth<SpotifyProfile>();
    if (block) return block;
    return this.http.get<SpotifyProfile>(`${this.getSecurityBase()}/me/spotify/profile`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /** Lecture en cours Spotify (pour affichage temps réel) */
  getSpotifyNowPlaying(): Observable<SpotifyNowPlaying> {
    const block = this.requireAuth<SpotifyNowPlaying>();
    if (block) return block;
    return this.http.get<SpotifyNowPlaying>(`${this.getSecurityBase()}/me/spotify/now-playing`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /** URL de connexion Discord (lier le compte) — requiert auth */
  getDiscordLoginUrl(): Observable<AuthResponse> {
    const block = this.requireAuth<AuthResponse>();
    if (block) return block;
    return this.http.get<AuthResponse>(`${this.getSecurityBase()}/auth/discord/login`, { withCredentials: true });
  }

  /** Statut de liaison Discord (GET /me/discord) */
  getDiscordStatus(): Observable<DiscordLinkStatus> {
    const block = this.requireAuth<DiscordLinkStatus>();
    if (block) return block;
    return this.http.get<DiscordLinkStatus>(`${this.getSecurityBase()}/me/discord`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getNightbotLoginUrl(): Observable<AuthResponse> {
    const block = this.requireAuth<AuthResponse>();
    if (block) return block;
    return this.http.get<AuthResponse>(`${this.getSecurityBase()}/auth/nightbot/login`, { withCredentials: true });
  }

  unlinkNightbot(): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/auth/nightbot/unlink`, null, { withCredentials: true });
  }

  getNightbotStatus(): Observable<NightbotLinkStatus> {
    const block = this.requireAuth<NightbotLinkStatus>();
    if (block) return block;
    return this.http.get<NightbotLinkStatus>(`${this.getSecurityBase()}/me/nightbot`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getNightbotImportData(): Observable<NightbotImportData> {
    const block = this.requireAuth<NightbotImportData>();
    if (block) return block;
    return this.http.get<NightbotImportData>(`${this.getSecurityBase()}/me/nightbot/import-data`, {
      withCredentials: true
    });
  }

  getStreamElementsImportData(payload: { jwtToken: string; includeDefaultCommands?: boolean; language?: string }): Observable<StreamElementsImportData> {
    const block = this.requireAuth<StreamElementsImportData>();
    if (block) return block;
    return this.http.post<StreamElementsImportData>(`${this.getSecurityBase()}/me/streamelements/import-data`, payload, {
      withCredentials: true
    });
  }

  /** Messages automatiques du bot (CRUD) */
  getAutoMessages(): Observable<AutoMessage[]> {
    const block = this.requireAuth<AutoMessage[]>();
    if (block) return block;
    return this.http.get<AutoMessage[]>(`${this.getSecurityBase()}/me/auto-messages`, {
      withCredentials: true
    });
  }

  createAutoMessage(payload: Omit<AutoMessage, 'id' | 'lastSentAt'>): Observable<AutoMessage> {
    const block = this.requireAuth<AutoMessage>();
    if (block) return block;
    return this.http.post<AutoMessage>(`${this.getSecurityBase()}/me/auto-messages`, payload, {
      withCredentials: true
    });
  }

  updateAutoMessage(id: string, payload: Omit<AutoMessage, 'id' | 'lastSentAt'>): Observable<AutoMessage> {
    const block = this.requireAuth<AutoMessage>();
    if (block) return block;
    return this.http.put<AutoMessage>(`${this.getSecurityBase()}/me/auto-messages/${id}`, payload, {
      withCredentials: true
    });
  }

  deleteAutoMessage(id: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/auto-messages/${id}`, {
      withCredentials: true
    });
  }

  /** Configuration de moderation Twitch (dashboard). */
  getTwitchModerationSettings(): Observable<TwitchModerationSettings> {
    const block = this.requireAuth<TwitchModerationSettings>();
    if (block) return block;
    return this.http.get<TwitchModerationSettings>(`${this.getSecurityBase()}/me/twitch/moderation-settings`, {
      withCredentials: true
    });
  }

  getTwitchModerationAnalytics(days = 14, granularitySeconds?: number | null, from?: string | null, to?: string | null): Observable<TwitchModerationAnalyticsOverview> {
    const block = this.requireAuth<TwitchModerationAnalyticsOverview>();
    if (block) return block;
    const params: Record<string, string> = { days: String(Math.min(Math.max(days, 1), 90)) };
    if (granularitySeconds) params['granularitySeconds'] = String(Math.max(1, Math.floor(granularitySeconds)));
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.http.get<TwitchModerationAnalyticsOverview>(`${this.getSecurityBase()}/me/twitch/moderation-analytics`, {
      withCredentials: true,
      params,
      context: createSkipGlobalLoaderContext()
    });
  }

  saveTwitchModerationSettings(payload: TwitchModerationSettings): Observable<TwitchModerationSettings> {
    const block = this.requireAuth<TwitchModerationSettings>();
    if (block) return block;
    return this.http.put<TwitchModerationSettings>(`${this.getSecurityBase()}/me/twitch/moderation-settings`, payload, {
      withCredentials: true
    });
  }

	getLevelSettings(): Observable<LevelSettings> {
		const block = this.requireAuth<LevelSettings>();
		if (block) return block;
		return this.http.get<LevelSettings>(`${this.getSecurityBase()}/me/levels/settings`, {
			withCredentials: true
		});
	}

	saveLevelSettings(payload: LevelSettings): Observable<LevelSettings> {
		const block = this.requireAuth<LevelSettings>();
		if (block) return block;
		return this.http.put<LevelSettings>(`${this.getSecurityBase()}/me/levels/settings`, payload, {
			withCredentials: true
		});
	}

	resetLevelSystem(): Observable<LevelResetResponse> {
		const block = this.requireAuth<LevelResetResponse>();
		if (block) return block;
		return this.http.post<LevelResetResponse>(`${this.getSecurityBase()}/me/levels/reset`, {}, {
			withCredentials: true
		});
	}

	getTwitchFollowers(page = 0, size = 50, search = ''): Observable<TwitchFollowerPage> {
		const block = this.requireAuth<TwitchFollowerPage>();
		if (block) return block;
		const params = new URLSearchParams({
			page: String(Math.max(0, Math.floor(page))),
			size: String(Math.min(Math.max(size, 1), 100))
		});
		const normalizedSearch = (search ?? '').trim();
		if (normalizedSearch) {
			params.set('search', normalizedSearch);
		}
		return this.http.get<TwitchFollowerPage>(`${this.getSecurityBase()}/me/twitch/followers?${params.toString()}`, {
			withCredentials: true
		});
	}

	getTwitchFollowerHygiene(page = 0, size = 50, search = '', suspectsOnly = false): Observable<TwitchFollowerHygienePage> {
		const block = this.requireAuth<TwitchFollowerHygienePage>();
		if (block) return block;
		const params = new URLSearchParams({
			page: String(Math.max(0, Math.floor(page))),
			size: String(Math.min(Math.max(size, 1), 100)),
			suspectsOnly: String(Boolean(suspectsOnly))
		});
		const normalizedSearch = (search ?? '').trim();
		if (normalizedSearch) {
			params.set('search', normalizedSearch);
		}
		return this.http.get<TwitchFollowerHygienePage>(`${this.getSecurityBase()}/me/twitch/followers/hygiene?${params.toString()}`, {
			withCredentials: true
		});
	}

	updateTwitchFollowerHygiene(followerUserId: string, status: string, note?: string | null): Observable<TwitchFollowerHygieneDecisionResponse> {
		const block = this.requireAuth<TwitchFollowerHygieneDecisionResponse>();
		if (block) return block;
		return this.http.patch<TwitchFollowerHygieneDecisionResponse>(
			`${this.getSecurityBase()}/me/twitch/followers/${encodeURIComponent(followerUserId)}/hygiene`,
			{status, note: note ?? null},
			{withCredentials: true}
		);
	}

	scanTwitchFollowerHygiene(): Observable<TwitchFollowerHygieneScanResponse> {
		const block = this.requireAuth<TwitchFollowerHygieneScanResponse>();
		if (block) return block;
		return this.http.post<TwitchFollowerHygieneScanResponse>(`${this.getSecurityBase()}/me/twitch/followers/hygiene/scan`, null, {
			withCredentials: true
		});
	}

	applyTwitchFollowerHygieneBulkAction(payload: {
		followerUserIds: string[];
		action?: string | null;
		status?: string | null;
		durationSeconds?: number | null;
		reason?: string | null;
		note?: string | null;
	}): Observable<TwitchFollowerHygieneBulkActionResponse> {
		const block = this.requireAuth<TwitchFollowerHygieneBulkActionResponse>();
		if (block) return block;
		return this.http.post<TwitchFollowerHygieneBulkActionResponse>(
			`${this.getSecurityBase()}/me/twitch/followers/hygiene/bulk-actions`,
			payload,
			{withCredentials: true}
		);
	}

	applyTwitchFollowerHygieneAction(
		followerUserId: string,
		action: string,
		durationSeconds?: number | null,
		reason?: string | null
	): Observable<TwitchFollowerHygieneActionResponse> {
		const block = this.requireAuth<TwitchFollowerHygieneActionResponse>();
		if (block) return block;
		return this.http.post<TwitchFollowerHygieneActionResponse>(
			`${this.getSecurityBase()}/me/twitch/followers/${encodeURIComponent(followerUserId)}/hygiene/actions`,
			{action, durationSeconds: durationSeconds ?? null, reason: reason ?? null},
			{withCredentials: true}
		);
	}

	syncTwitchFollowers(): Observable<TwitchFollowerSyncResponse> {
		const block = this.requireAuth<TwitchFollowerSyncResponse>();
		if (block) return block;
		return this.http.post<TwitchFollowerSyncResponse>(`${this.getSecurityBase()}/me/twitch/followers/sync`, null, {
			withCredentials: true
		});
	}

	getLevelPanelPreview(limit?: number, period?: 'week' | 'month' | 'global'): Observable<LevelPanelPreview> {
		const block = this.requireAuth<LevelPanelPreview>();
		if (block) return block;
		const params: Record<string, string> = {};
		if (limit && limit > 0) {
			params['limit'] = String(Math.min(Math.max(limit, 1), 50));
		}
		if (period) {
			params['period'] = period;
		}
		return this.http.get<LevelPanelPreview>(`${this.getSecurityBase()}/me/levels/panel-preview`, {
			withCredentials: true,
			params,
			context: createSkipGlobalLoaderContext()
		});
	}

	getPublicLevelPanel(userId: string, limit?: number, period?: 'week' | 'month' | 'global'): Observable<LevelPanelPreview> {
		const base = this.getBatchBaseUrl();
		const params: Record<string, string> = {userId: userId.trim()};
		if (limit && limit > 0) {
			params['limit'] = String(Math.min(Math.max(limit, 1), 50));
		}
		if (period) {
			params['period'] = period;
		}
		return this.http.get<LevelPanelPreview>(`${base}/batch/levels/panel`, {
			params,
			context: createSkipGlobalLoaderContext()
		});
	}

	getPublicStreamerPanel(
		streamer: string,
		levelLimit?: number,
		levelPeriod?: 'week' | 'month' | 'global',
		recentTrackLimit?: number
	): Observable<PublicStreamerPanel> {
		const params: Record<string, string> = {streamer: streamer.trim()};
		if (levelLimit && levelLimit > 0) {
			params['levelLimit'] = String(Math.min(Math.max(levelLimit, 1), 50));
		}
		if (levelPeriod) {
			params['levelPeriod'] = levelPeriod;
		}
		if (recentTrackLimit && recentTrackLimit > 0) {
			params['recentTrackLimit'] = String(Math.min(Math.max(recentTrackLimit, 1), 10));
		}
		return this.http.get<PublicStreamerPanel>(`${this.getSecurityBase()}/public/panel/streamer`, {
			params,
			context: this.createHttpContext({
				skipGlobalLoader: true,
				skipDelegation: true,
				skipSessionRefresh: true
			})
		});
	}

	watchPublicStreamerPanel(
		streamer: string,
		levelLimit?: number,
		levelPeriod?: 'week' | 'month' | 'global',
		recentTrackLimit?: number
	): Observable<PublicStreamerPanel> {
		return new Observable<PublicStreamerPanel>((subscriber) => {
			const socket = new WebSocket(this.buildPublicStreamerPanelWebSocketUrl(streamer, levelLimit, levelPeriod, recentTrackLimit));
			let receivedMessage = false;

			socket.onmessage = (event) => {
				try {
					receivedMessage = true;
					subscriber.next(JSON.parse(event.data) as PublicStreamerPanel);
				} catch (error) {
					subscriber.error(error);
				}
			};
			socket.onerror = () => subscriber.error(new Error('Public streamer panel websocket error'));
			socket.onclose = (event) => {
				if ((!event.wasClean || !receivedMessage) && !subscriber.closed) {
					subscriber.error(new Error('Public streamer panel websocket closed unexpectedly'));
				} else if (!subscriber.closed) {
					subscriber.complete();
				}
			};

			return () => {
				if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
					socket.close();
				}
			};
		});
	}

	private buildPublicStreamerPanelWebSocketUrl(
		streamer: string,
		levelLimit?: number,
		levelPeriod?: 'week' | 'month' | 'global',
		recentTrackLimit?: number
	): string {
		const base = this.getSecurityBase()
			.replace(/^https:/i, 'wss:')
			.replace(/^http:/i, 'ws:')
			.replace(/\/+$/, '');
		const params = new URLSearchParams({streamer: streamer.trim()});
		if (levelLimit && levelLimit > 0) {
			params.set('levelLimit', String(Math.min(Math.max(levelLimit, 1), 50)));
		}
		if (levelPeriod) {
			params.set('levelPeriod', levelPeriod);
		}
		if (recentTrackLimit && recentTrackLimit > 0) {
			params.set('recentTrackLimit', String(Math.min(Math.max(recentTrackLimit, 1), 10)));
		}
		return `${base}/public/panel/streamer/ws?${params.toString()}`;
	}

  /** Profil Discord (username, avatar) — arrière-plan sans loader */
  getDiscordProfile(): Observable<DiscordProfile | null> {
    const block = this.requireAuth<DiscordProfile | null>();
    if (block) return block;
    return this.http.get<DiscordProfile>(`${this.getSecurityBase()}/me/discord/profile`, {
      withCredentials: true,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Liste des serveurs Discord (nom, membres, connectés) — arrière-plan sans loader */
  getDiscordGuilds(): Observable<DiscordGuild[]> {
    const block = this.requireAuth<DiscordGuild[]>();
    if (block) return block;
    return this.http.get<DiscordGuild[]>(`${this.getSecurityBase()}/me/discord/guilds`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    }).pipe(
      catchError(() => of([]))
    );
  }

  /** Délier le compte Discord */
  unlinkDiscord(): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/auth/discord/unlink`, null, { withCredentials: true });
  }

  /** Statistiques Discord (connectés, inscrits) — optionnel */
  getDiscordStats(): Observable<DiscordStats> {
    const block = this.requireAuth<DiscordStats>();
    if (block) return block;
    return this.http.get<DiscordStats>(`${this.getSecurityBase()}/me/discord/stats`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /** URL d'invitation du bot Discord (lier un serveur) — requiert auth */
  getDiscordInviteBotUrl(): Observable<{ url: string }> {
    const block = this.requireAuth<{ url: string }>();
    if (block) return block;
    return this.http.get<{ url: string }>(`${this.getSecurityBase()}/auth/discord/invite-bot`, { withCredentials: true });
  }

  /** Serveur Discord lié (pour événement au go live) */
  getDiscordLinkedServer(): Observable<DiscordLinkedServer | null> {
    const block = this.requireAuth<DiscordLinkedServer | null>();
    if (block) return block;
    return this.http.get<DiscordLinkedServer>(`${this.getSecurityBase()}/me/discord/linked-server`, {
      withCredentials: true,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Liste des serveurs Discord lies pour le compte courant. */
  getDiscordLinkedServers(): Observable<DiscordLinkedServer[]> {
    const block = this.requireAuth<DiscordLinkedServer[]>();
    if (block) return block;
    return this.http.get<DiscordLinkedServer[]>(`${this.getSecurityBase()}/me/discord/linked-servers`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    }).pipe(
      catchError(() => of([]))
    );
  }

  /** Activer/désactiver la création d'événement Discord au go live */
  updateDiscordLinkedServer(createEventOnGoLive: boolean): Observable<DiscordLinkedServer | null> {
    const block = this.requireAuth<DiscordLinkedServer | null>();
    if (block) return block;
    return this.http.put<DiscordLinkedServer>(`${this.getSecurityBase()}/me/discord/linked-server`, { createEventOnGoLive }, {
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Lie un serveur Discord supplementaire par son ID. */
  createDiscordLinkedServer(guildId: string): Observable<DiscordLinkedServer | null> {
    const block = this.requireAuth<DiscordLinkedServer | null>();
    if (block) return block;
    return this.http.post<DiscordLinkedServer>(`${this.getSecurityBase()}/me/discord/linked-servers`, { guildId: guildId?.trim() }, {
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(res => res.status >= 200 && res.status < 300 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Met a jour un serveur Discord lie. */
  updateDiscordLinkedServerById(linkedServerId: string, payload: { createEventOnGoLive?: boolean; primary?: boolean }): Observable<DiscordLinkedServer | null> {
    const block = this.requireAuth<DiscordLinkedServer | null>();
    if (block) return block;
    return this.http.patch<DiscordLinkedServer>(`${this.getSecurityBase()}/me/discord/linked-servers/${encodeURIComponent(linkedServerId)}`, payload, {
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Lier un serveur Discord par son ID (contournement si la redirection OAuth2 Discord échoue) */
  linkDiscordServerByGuildId(guildId: string): Observable<DiscordLinkedServer | null> {
    return this.createDiscordLinkedServer(guildId);
  }

  /** Délier le serveur Discord */
  unlinkDiscordServer(): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/discord/linked-server`, { withCredentials: true });
  }

  /** Delie un serveur Discord specifique. */
  unlinkDiscordLinkedServer(linkedServerId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/discord/linked-servers/${encodeURIComponent(linkedServerId)}`, { withCredentials: true });
  }

  /** Délégations reçues: comptes que l'utilisateur courant gère. */
  getDelegationsAssignedToMe(): Observable<Delegation[]> {
    const block = this.requireAuth<Delegation[]>();
    if (block) return block;
    return this.http.get<Delegation[]>(`${this.getSecurityBase()}/me/delegations`, {
      withCredentials: true
    }).pipe(catchError(() => of([])));
  }

  /** Délégations accordées: personnes autorisées à gérer mon compte. */
  getDelegationsOwnedByMe(): Observable<Delegation[]> {
    const block = this.requireAuth<Delegation[]>();
    if (block) return block;
    return this.http.get<Delegation[]>(`${this.getSecurityBase()}/me/delegations/owned`, {
      withCredentials: true
    }).pipe(catchError(() => of([])));
  }

  /** Invitations en attente reçues par l'utilisateur courant. */
  getDelegationInvitations(): Observable<DelegationInvitation[]> {
    const block = this.requireAuth<DelegationInvitation[]>();
    if (block) return block;
    return this.http.get<DelegationInvitation[]>(`${this.getSecurityBase()}/me/delegations/invitations`, {
      withCredentials: true
    }).pipe(catchError(() => of([])));
  }

  /** Crée une invitation de délégation. */
  createDelegationInvitation(username: string, permissions: string[]): Observable<DelegationInvitation> {
    const block = this.requireAuth<DelegationInvitation>();
    if (block) return block;
    return this.http.post<DelegationInvitation>(`${this.getSecurityBase()}/me/delegations/invitations`, {
      username: (username ?? '').trim(),
      permissions
    }, { withCredentials: true });
  }

  /** Accepte une invitation de délégation. */
  acceptDelegationInvitation(invitationId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/me/delegations/invitations/${encodeURIComponent(invitationId)}/accept`, null, {
      withCredentials: true,
      observe: 'response'
    }).pipe(map(() => undefined));
  }

  /** Rejette une invitation de délégation. */
  rejectDelegationInvitation(invitationId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/me/delegations/invitations/${encodeURIComponent(invitationId)}/reject`, null, {
      withCredentials: true,
      observe: 'response'
    }).pipe(map(() => undefined));
  }

  /** Révoque une délégation accordée à un gestionnaire. */
  revokeDelegation(managerUserId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/delegations/${encodeURIComponent(managerUserId)}`, {
      withCredentials: true
    });
  }

  /** Config publication du planning sur Discord (GET /me/schedule-discord). */
  getScheduleDiscordConfig(guildId?: string): Observable<ScheduleDiscordConfig | null> {
    const block = this.requireAuth<ScheduleDiscordConfig | null>();
    if (block) return block;
    const params = guildId ? new HttpParams().set('guildId', guildId) : undefined;
    return this.http.get<ScheduleDiscordConfig>(`${this.getSecurityBase()}/me/schedule-discord`, {
      withCredentials: true,
      params,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  /** Enregistrer la config publication planning Discord (PUT /me/schedule-discord). */
  saveScheduleDiscordConfig(dto: Partial<ScheduleDiscordConfig>): Observable<ScheduleDiscordConfig> {
    const block = this.requireAuth<ScheduleDiscordConfig>();
    if (block) return block;
    return this.http.put<ScheduleDiscordConfig>(`${this.getSecurityBase()}/me/schedule-discord`, dto, { withCredentials: true });
  }

  getDiscordLiveAnnouncementConfig(guildId?: string): Observable<DiscordLiveAnnouncementConfig | null> {
    const block = this.requireAuth<DiscordLiveAnnouncementConfig | null>();
    if (block) return block;
    const params = guildId ? new HttpParams().set('guildId', guildId) : undefined;
    return this.http.get<DiscordLiveAnnouncementConfig>(`${this.getSecurityBase()}/me/discord/live-announcements`, {
      withCredentials: true,
      params,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  saveDiscordLiveAnnouncementConfig(dto: Partial<DiscordLiveAnnouncementConfig>): Observable<DiscordLiveAnnouncementConfig> {
    const block = this.requireAuth<DiscordLiveAnnouncementConfig>();
    if (block) return block;
    return this.http.put<DiscordLiveAnnouncementConfig>(`${this.getSecurityBase()}/me/discord/live-announcements`, dto, { withCredentials: true });
  }

  getDiscordClipAnnouncementConfig(guildId?: string): Observable<DiscordClipAnnouncementConfig | null> {
    const block = this.requireAuth<DiscordClipAnnouncementConfig | null>();
    if (block) return block;
    const params = guildId ? new HttpParams().set('guildId', guildId) : undefined;
    return this.http.get<DiscordClipAnnouncementConfig>(`${this.getSecurityBase()}/me/discord/clip-announcements`, {
      withCredentials: true,
      params,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(res => res.status === 200 && res.body ? res.body : null),
      catchError(() => of(null))
    );
  }

  saveDiscordClipAnnouncementConfig(dto: Partial<DiscordClipAnnouncementConfig>): Observable<DiscordClipAnnouncementConfig> {
    const block = this.requireAuth<DiscordClipAnnouncementConfig>();
    if (block) return block;
    return this.http.put<DiscordClipAnnouncementConfig>(`${this.getSecurityBase()}/me/discord/clip-announcements`, dto, { withCredentials: true });
  }

  /** Liste des canaux d'un serveur Discord (pour choix du canal de publication). */
  getScheduleDiscordChannels(guildId: string): Observable<DiscordChannelInfo[]> {
    const block = this.requireAuth<DiscordChannelInfo[]>();
    if (block) return block;
    return this.http.get<DiscordChannelInfo[]>(`${this.getSecurityBase()}/me/schedule-discord/guilds/${encodeURIComponent(guildId)}/channels`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    }).pipe(catchError(() => of([])));
  }

  /** Publier le planning sur le canal Discord configuré (POST /me/schedule-discord/publish). */
  publishScheduleDiscord(guildId?: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    const params = guildId ? new HttpParams().set('guildId', guildId) : undefined;
    return this.http.post<void>(`${this.getSecurityBase()}/me/schedule-discord/publish`, null, {
      withCredentials: true,
      params,
      observe: 'response'
    }).pipe(map(() => undefined));
  }

  /**
   * Récupère l'utilisateur courant via l'API de sécurité (freyr-security).
   * Auth par cookie freyr_session. Optionnellement, passer le token (ex. juste après callback)
   * pour authentifier ce seul appel sans attendre que le cookie soit pris en compte.
   *
   * @param skipDelegation si true, n'envoie pas les en-têtes de délégation / prévisualisation admin (identité JWT uniquement).
   *   À utiliser pour l'amorçage session (garde, AppComponent, callback) afin d'éviter un 403 si une délégation périmée
   *   est encore en sessionStorage avant application du query param adminPreviewUserId.
   */
  getCurrentUser(token?: string, options?: { skipDelegation?: boolean }): Observable<User> {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    const headers = token
      ? new HttpHeaders().set('Authorization', `Bearer ${token}`)
      : new HttpHeaders();
    const skipDelegation = options?.skipDelegation ?? false;
    const context = skipDelegation ? createSkipDelegationContext() : undefined;
    return this.http.get<User>(`${base}/me`, {
      headers,
      withCredentials: true,
      ...(context ? { context } : {})
    });
  }

  getSiteAccessStatus(): Observable<SiteAccessStatus> {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return this.http.get<SiteAccessStatus>(`${base}/auth/site-access`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getCommandVariableCatalog(): Observable<CommandVariableCatalogItem[]> {
    const block = this.requireAuth<CommandVariableCatalogItem[]>();
    if (block) return block;
    return this.http.get<CommandVariableCatalogItem[]>(`${this.getSecurityBase()}/me/twitch/command-variable-catalog`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    }).pipe(
      catchError(() => of([]))
    );
  }

  getAccountSecurityStatus(): Observable<AccountSecurityStatus> {
    const block = this.requireAuth<AccountSecurityStatus>();
    if (block) return block;
    return this.http.get<AccountSecurityStatus>(`${this.getSecurityBase()}/me/security`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  updateLocalCredentials(email: string, password: string): Observable<AccountSecurityStatus> {
    const block = this.requireAuth<AccountSecurityStatus>();
    if (block) return block;
    return this.http.put<AccountSecurityStatus>(`${this.getSecurityBase()}/me/security/local-credentials`, {
      email,
      password
    }, {
      withCredentials: true
    });
  }

  resendAccountVerification(): Observable<AccountSecurityStatus> {
    const block = this.requireAuth<AccountSecurityStatus>();
    if (block) return block;
    return this.http.post<AccountSecurityStatus>(`${this.getSecurityBase()}/me/security/resend-verification`, null, {
      withCredentials: true
    });
  }

  getFeatureFlags(): Observable<FeatureFlag[]> {
    const block = this.requireAuth<FeatureFlag[]>();
    if (block) return block;
    return this.http.get<FeatureFlag[]>(`${this.getSecurityBase()}/me/feature-flags`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  exchangeSupportToken(token: string): Observable<SupportExchangeResponse> {
    const block = this.requireAuth<SupportExchangeResponse>();
    if (block) return block;
    return this.http.post<SupportExchangeResponse>(`${this.getSecurityBase()}/auth/support/exchange`, { token }, {
      withCredentials: true,
      context: this.createHttpContext({
        skipDelegation: true
      })
    });
  }

  closeSupportSession(supportSessionId: string, reason?: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.post<void>(`${this.getSecurityBase()}/auth/support/close`, {
      supportSessionId,
      reason: reason ?? ''
    }, {
      withCredentials: true,
      context: this.createHttpContext({
        skipGlobalLoader: true,
        skipDelegation: true
      }),
      observe: 'response'
    }).pipe(map(() => undefined));
  }

  getSupportNotices(): Observable<SupportNotice[]> {
    const block = this.requireAuth<SupportNotice[]>();
    if (block) return block;
    return this.http.get<SupportNotice[]>(`${this.getSecurityBase()}/me/support-notices`, {
      withCredentials: true,
      context: this.createHttpContext({
        skipGlobalLoader: true,
        skipDelegation: true
      })
    }).pipe(
      catchError(() => of([]))
    );
  }

  /** Données Twitch du compte lié (planning, streams, clips, chaîne) — requiert authentification */
  private getMeTwitchBase(): string {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return `${base}/me/twitch`;
  }

  private createHttpContext(options?: HttpRequestContextOptions): HttpContext | undefined {
    const skipGlobalLoader = options?.skipGlobalLoader ?? false;
    const skipDelegation = options?.skipDelegation ?? false;
    const skipSessionRefresh = options?.skipSessionRefresh ?? false;
    if (!skipGlobalLoader && !skipDelegation && !skipSessionRefresh) {
      return undefined;
    }

    let context = new HttpContext();
    if (skipGlobalLoader) {
      context = context.set(SKIP_GLOBAL_LOADER, true);
    }
      if (skipDelegation) {
        context = context.set(SKIP_DELEGATION_CONTEXT, true);
      }
      if (skipSessionRefresh) {
        context = context.set(SKIP_SESSION_REFRESH, true);
      }
      return context;
    }

  getTwitchSchedule(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/schedule`, { withCredentials: true });
  }

  searchTwitchCategories(query: string): Observable<TwitchCategory[]> {
    const block = this.requireAuth<TwitchCategory[]>();
    if (block) return block;
    const value = (query ?? '').trim();
    if (!value) {
      return of([]);
    }
    return this.http.get<{ data?: TwitchCategory[] }>(`${this.getMeTwitchBase()}/schedule/categories`, {
      withCredentials: true,
      params: { query: value },
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(resp => Array.isArray(resp?.data) ? resp.data : []),
      catchError(() => of([]))
    );
  }

  /** Créer un créneau dans le planning Twitch (scope channel:manage:schedule). */
  createScheduleSegment(body: { start_time: string; timezone: string; duration: string; title: string; category_id?: string; is_recurring?: boolean }): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.post<Record<string, unknown>>(`${this.getMeTwitchBase()}/schedule/segments`, body, { withCredentials: true });
  }

  /** Modifier un créneau du planning Twitch. */
  updateScheduleSegment(segmentId: string, body: Partial<{ start_time: string; timezone: string; duration: string; title: string; category_id: string; is_recurring: boolean }>): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.patch<Record<string, unknown>>(`${this.getMeTwitchBase()}/schedule/segments/${segmentId}`, body, { withCredentials: true });
  }

  /** Supprimer un créneau du planning Twitch. */
  deleteScheduleSegment(segmentId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getMeTwitchBase()}/schedule/segments/${segmentId}`, { withCredentials: true });
  }

  getTwitchStreams(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/streams`, { withCredentials: true });
  }

  getTwitchClips(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/clips`, { withCredentials: true });
  }

  getTwitchVideos(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/videos`, { withCredentials: true });
  }

  getTwitchChannel(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/channel`, { withCredentials: true });
  }

  /** Liste des modérateurs de la chaîne (scope moderation:read). */
  getTwitchModerators(): Observable<{ data: TwitchChannelRoleUser[] }> {
    const block = this.requireAuth<{ data: TwitchChannelRoleUser[] }>();
    if (block) return block;
    return this.http.get<{ data: TwitchChannelRoleUser[] }>(`${this.getMeTwitchBase()}/moderators`, { withCredentials: true });
  }

  /** Liste des VIP de la chaîne (scope channel:read:vips). */
  getTwitchVips(): Observable<{ data: TwitchChannelRoleUser[] }> {
    const block = this.requireAuth<{ data: TwitchChannelRoleUser[] }>();
    if (block) return block;
    return this.http.get<{ data: TwitchChannelRoleUser[] }>(`${this.getMeTwitchBase()}/vips`, { withCredentials: true });
  }

  /** Liste des éditeurs de la chaîne (scope channel:read:editors). */
  getTwitchEditors(): Observable<{ data: TwitchChannelRoleUser[] }> {
    const block = this.requireAuth<{ data: TwitchChannelRoleUser[] }>();
    if (block) return block;
    return this.http.get<{ data: TwitchChannelRoleUser[] }>(`${this.getMeTwitchBase()}/editors`, { withCredentials: true });
  }

  /** URLs des icônes des badges de chat Twitch (moderator, vip, subscriber, broadcaster). */
  getTwitchChatBadges(): Observable<Record<string, string>> {
    const block = this.requireAuth<Record<string, string>>();
    if (block) return block;
    return this.http.get<Record<string, string>>(`${this.getMeTwitchBase()}/chat-badges`, { withCredentials: true });
  }

  /** Profil Twitch (bannière, broadcaster_type partenaire/affilié) */
  getTwitchUser(): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/users`, { withCredentials: true });
  }

  getTwitchUserInBackground(options?: { skipDelegation?: boolean }): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.get<Record<string, unknown>>(`${this.getMeTwitchBase()}/users`, {
      withCredentials: true,
      context: this.createHttpContext({
        skipGlobalLoader: true,
        skipDelegation: options?.skipDelegation ?? false
      })
    });
  }

  /** Scopes OAuth du compte Twitch lié (pour affichage sur le profil) */
  getTwitchScopes(options?: { skipDelegation?: boolean }): Observable<TwitchScopeStatus> {
    const block = this.requireAuth<TwitchScopeStatus>();
    if (block) return block;
    return this.http.get<TwitchScopeStatus>(`${this.getMeTwitchBase()}/scopes`, {
      withCredentials: true,
      context: this.createHttpContext({
        skipGlobalLoader: true,
        skipDelegation: options?.skipDelegation ?? false
      })
    });
  }

  /** URL de déconnexion : le front doit appeler avec fetch(..., { credentials: 'include', redirect: 'manual' }) puis clear state et rediriger. */
  getLogoutUrl(): string {
    const base = this.securityApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
    return `${base}/auth/logout`;
  }

  getStats(): Observable<CommandStats> {
    const block = this.requireAuth<CommandStats>();
    if (block) return block;
    return this.http.get<CommandStats>(`${this.apiUrl}/stats`, { withCredentials: true });
  }

  /** Même que getStats() mais sans déclencher le loader global (rafraîchissement en arrière-plan). */
  getStatsInBackground(): Observable<CommandStats> {
    const block = this.requireAuth<CommandStats>();
    if (block) return block;
    return this.http.get<CommandStats>(`${this.apiUrl}/stats`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  subscribeCommandStats(): Observable<CommandStats> {
    const block = this.requireAuth<CommandStats>();
    if (block) return block;

    return new Observable<CommandStats>((subscriber) => {
      const eventSource = new EventSource(`${this.apiUrl}/stats/stream`, { withCredentials: true });
      eventSource.addEventListener('command-stats', (event) => {
        try {
          subscriber.next(JSON.parse((event as MessageEvent).data) as CommandStats);
        } catch (error) {
          subscriber.error(error);
        }
      });
      eventSource.onerror = () => {
        eventSource.close();
        subscriber.error(new Error('Command stats stream disconnected'));
      };
      return () => eventSource.close();
    });
  }

  /** Envoie une commande au bot. Retourne status et resolvedMessage si une définition personnalisée a été utilisée. */
  sendCommand(command: string, platform: string, parameters?: string): Observable<{ status: string; resolvedMessage?: string }> {
    const block = this.requireAuth<{ status: string; resolvedMessage?: string }>();
    if (block) return block;
    return this.http.post<{ status: string; resolvedMessage?: string }>(
      `${this.apiUrl}/commands`,
      { command, platform, parameters: parameters ?? '' },
      { withCredentials: true }
    );
  }

  /** Liste des définitions de commandes de l'utilisateur */
  listCommandDefinitions(): Observable<CommandDefinition[]> {
    const block = this.requireAuth<CommandDefinition[]>();
    if (block) return block;
    return this.http.get<CommandDefinition[]>(`${this.apiUrl}/command-definitions`, { withCredentials: true });
  }

  /** Créer une définition de commande */
  createCommandDefinition(body: {
    name: string;
    platform: string;
    responseTemplate: string;
    cooldownSeconds?: number;
    enabled?: boolean;
    availableWhen?: string;
    allowedRoles?: string;
    cooldownExemptRoles?: string;
    keywords?: string;
    aliases?: string;
  }): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.post<Record<string, unknown>>(`${this.apiUrl}/command-definitions`, body, { withCredentials: true });
  }

  /** Modifier une définition de commande */
  updateCommandDefinition(id: string, body: Partial<{ name: string; platform: string; responseTemplate: string; cooldownSeconds: number; enabled: boolean; availableWhen: string; allowedRoles: string; cooldownExemptRoles: string; keywords: string; aliases: string }>): Observable<Record<string, unknown>> {
    const block = this.requireAuth<Record<string, unknown>>();
    if (block) return block;
    return this.http.put<Record<string, unknown>>(`${this.apiUrl}/command-definitions/${id}`, body, { withCredentials: true });
  }

  /** Supprimer une définition de commande */
  deleteCommandDefinition(id: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.apiUrl}/command-definitions/${id}`, { withCredentials: true });
  }

  /** Paramètres des commandes intégrées (activation, template personnalisé). */
  listDefaultCommandSettings(): Observable<DefaultCommandSetting[]> {
    const block = this.requireAuth<DefaultCommandSetting[]>();
    if (block) return block;
    return this.http.get<DefaultCommandSetting[]>(`${this.apiUrl}/default-command-settings`, { withCredentials: true });
  }

  /** Mettre à jour activation et/ou template d'une commande intégrée. commandKey sans "!" (ex. uptime, cmd). */
  updateDefaultCommandSetting(commandKey: string, body: { enabled?: boolean; responseTemplate?: string | null }): Observable<DefaultCommandSetting> {
    const block = this.requireAuth<DefaultCommandSetting>();
    if (block) return block;
    return this.http.put<DefaultCommandSetting>(`${this.apiUrl}/default-command-settings/${commandKey}`, body, { withCredentials: true });
  }

  /** Statut du bot IRC et diagnostic chatbot Twitch pour le canal utilisateur. */
  getTwitchIrcChannelStatus(userId: string): Observable<{
    registered: boolean;
    connected?: boolean;
    chatbotChannelLogin?: string;
    chatbotBroadcasterId?: string;
    chatbotEventsubActive?: boolean;
    chatbotHelixOnly?: boolean;
    chatbotAppTokenAvailable?: boolean;
    chatbotUserTokenAvailable?: boolean;
    chatbotSenderUserIdAvailable?: boolean;
    chatbotSenderUserId?: string;
    chatbotDeliveryTokenMode?: string;
    chatbotComplianceState?: string;
  }> {
    const block = this.requireAuth<{
      registered: boolean;
      connected?: boolean;
      chatbotChannelLogin?: string;
      chatbotBroadcasterId?: string;
      chatbotEventsubActive?: boolean;
      chatbotHelixOnly?: boolean;
      chatbotAppTokenAvailable?: boolean;
      chatbotUserTokenAvailable?: boolean;
      chatbotSenderUserIdAvailable?: boolean;
      chatbotSenderUserId?: string;
      chatbotDeliveryTokenMode?: string;
      chatbotComplianceState?: string;
    }>();
    if (block) return block;
    return this.http.get<{
      registered: boolean;
      connected?: boolean;
      chatbotChannelLogin?: string;
      chatbotBroadcasterId?: string;
      chatbotEventsubActive?: boolean;
      chatbotHelixOnly?: boolean;
      chatbotAppTokenAvailable?: boolean;
      chatbotUserTokenAvailable?: boolean;
      chatbotSenderUserIdAvailable?: boolean;
      chatbotSenderUserId?: string;
      chatbotDeliveryTokenMode?: string;
      chatbotComplianceState?: string;
    }>(`${this.apiUrl}/irc/channels/status`, {
      params: { userId },
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /** Demande au bot IRC (compte dédié) de rejoindre le canal Twitch de l'utilisateur connecté (canal résolu via compte lié). */
  registerTwitchIrcChannel(userId: string): Observable<TwitchIrcRegisterResponse> {
    const block = this.requireAuth<TwitchIrcRegisterResponse>();
    if (block) return block;
    return this.http.post<TwitchIrcRegisterResponse>(`${this.apiUrl}/irc/channels/register`, { userId }, { withCredentials: true });
  }

  /** Désactive le bot IRC sur le canal de l'utilisateur. */
  unregisterTwitchIrcChannel(userId: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.apiUrl}/irc/channels/register`, {
      params: { userId },
      withCredentials: true
    });
  }

  getStreamTitleSettings(): Observable<StreamTitleStatus> {
    return this.http.get<StreamTitleStatus>(`${this.apiUrl}/stream-title`, {withCredentials: true, context: createSkipDelegationContext()});
  }
  saveStreamTitleSettings(template: string, automatic: boolean): Observable<{saved: boolean}> {
    return this.http.put<{saved: boolean}>(`${this.apiUrl}/stream-title`, {template, automatic}, {withCredentials: true, context: createSkipDelegationContext()});
  }
  previewStreamTitle(template: string): Observable<StreamTitlePreview> {
    return this.http.post<StreamTitlePreview>(`${this.apiUrl}/stream-title/preview`, {template, automatic: false}, {withCredentials: true, context: createSkipDelegationContext()});
  }
  applyStreamTitle(): Observable<StreamTitlePreview> {
    return this.http.post<StreamTitlePreview>(`${this.apiUrl}/stream-title/apply`, {}, {withCredentials: true, context: createSkipDelegationContext()});
  }

  /** Résout un template avec les variables (twitch.*, freyr.*, minecraft.*) pour l'utilisateur connecté */
  resolveTemplate(template: string, simulation?:unknown): Observable<{ resolvedText: string; unresolvedVariables?:string[]; warnings?:string[]; contextMode?:string }> {
    const block = this.requireAuth<{ resolvedText: string; unresolvedVariables?:string[]; warnings?:string[]; contextMode?:string }>();
    if (block) return block;
    return this.http.post<{ resolvedText: string; unresolvedVariables?:string[]; warnings?:string[]; contextMode?:string }>(
      `${this.apiUrl}/commands/resolve-template`,
      { template, ...(simulation?{simulation}:{}) },
      { withCredentials: true }
    );
  }

  /** Top N commandes les plus utilisées par la communauté */
  getTopCommands(limit = 10): Observable<{ command: string; usageCount: number }[]> {
    const block = this.requireAuth<{ command: string; usageCount: number }[]>();
    if (block) return block;
    return this.http.get<{ command: string; usageCount: number }[]>(
      `${this.apiUrl}/commands/top?limit=${Math.min(Math.max(limit, 1), 100)}`,
      { withCredentials: true }
    );
  }

  /** Top raiders (nombre de raids et pic de viewers) pour le streamer connecté */
  getTopRaiders(period: 'week' | 'month' | 'global' = 'global'): Observable<TopRaider[]> {
    const block = this.requireAuth<TopRaider[]>();
    if (block) return block;
    return this.http.get<TopRaider[]>(`${this.apiUrl}/raids/top?period=${encodeURIComponent(period)}`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getTopViewers(period: 'week' | 'month' | 'global' = 'global'): Observable<TopViewer[]> {
    const block = this.requireAuth<TopViewer[]>();
    if (block) return block;
    return this.http.get<TopViewer[]>(`${this.apiUrl}/viewers/top?period=${encodeURIComponent(period)}`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getHomePageContent(): Observable<Map<string, string>> {
    return this.http.get<Map<string, string>>(`${this.apiUrl}/homepage/content`);
  }

  saveHomePageContent(sectionKey: string, content: string): Observable<any> {
    const block = this.requireAuth<any>();
    if (block) return block;
    return this.http.post(`${this.apiUrl}/homepage/content`, { sectionKey, content }, { withCredentials: true });
  }

  getAdminSiteAnalytics(days = 14): Observable<AdminSiteAnalyticsOverview> {
    const block = this.requireAuth<AdminSiteAnalyticsOverview>();
    if (block) return block;
    return this.http.get<AdminSiteAnalyticsOverview>(`${this.getSecurityBase()}/admin/site-analytics`, {
      withCredentials: true,
      params: { days: String(Math.min(Math.max(days, 1), 90)) },
      context: createSkipGlobalLoaderContext()
    });
  }

  getAdminTwitchModerationAnalytics(days = 14, granularitySeconds?: number | null, from?: string | null, to?: string | null): Observable<TwitchModerationAnalyticsOverview> {
    const block = this.requireAuth<TwitchModerationAnalyticsOverview>();
    if (block) return block;
    const params: Record<string, string> = { days: String(Math.min(Math.max(days, 1), 90)) };
    if (granularitySeconds) params['granularitySeconds'] = String(Math.max(1, Math.floor(granularitySeconds)));
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.http.get<TwitchModerationAnalyticsOverview>(`${this.getSecurityBase()}/admin/twitch/moderation-analytics`, {
      withCredentials: true,
      params,
      context: createSkipGlobalLoaderContext()
    });
  }

  deleteHomePageContent(sectionKey: string): Observable<any> {
    const block = this.requireAuth<any>();
    if (block) return block;
    return this.http.delete(`${this.apiUrl}/homepage/content/${sectionKey}`, { withCredentials: true });
  }

  /** Base URL du service batch (statut stream EventSub + SSE). */
  getBatchBaseUrl(): string {
    return this.batchApiUrl || this.apiUrl.replace(/\/api\/?$/, '');
  }

  /**
   * Statut du stream (freyr-batch, EventSub).
   * Polling : appeler périodiquement ou une fois.
   */
  getStreamStatus(broadcasterId: string): Observable<StreamStatus> {
    const base = this.getBatchBaseUrl();
    const url = `${base}/batch/stream-status?broadcasterId=${encodeURIComponent(broadcasterId)}`;
    return this.http.get<StreamStatus>(url, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /**
   * Enregistre le broadcaster pour le suivi EventSub (stream.online/offline).
   * À appeler une fois quand l'utilisateur a lié son Twitch (ex. au chargement du dashboard).
   */
  registerStreamSubscription(broadcasterId: string, freyrUserId: string): Observable<void> {
    const base = this.getBatchBaseUrl();
    const url = `${base}/batch/stream-status/subscriptions?broadcasterId=${encodeURIComponent(broadcasterId)}&freyrUserId=${encodeURIComponent(freyrUserId)}`;
    return this.http.post<void>(url, null, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  /**
   * S'abonner au flux SSE du statut du stream (mises à jour en temps réel).
   * Retourne un Observable qui émet à chaque mise à jour reçue du serveur.
   */
  subscribeStreamStatus(broadcasterId: string): Observable<StreamStatus> {
    return new Observable<StreamStatus>((subscriber) => {
      const base = this.getBatchBaseUrl();
      const url = `${base}/batch/stream-status/stream?broadcasterId=${encodeURIComponent(broadcasterId)}`;
      const eventSource = new EventSource(url);
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as StreamStatus;
          subscriber.next(data);
        } catch {
          // ignore
        }
      };
      eventSource.onerror = () => {
        eventSource.close();
        subscriber.complete();
      };
      return () => eventSource.close();
    });
  }

  getStreamChatAnalyticsByUserId(userId: string, limit = 5, granularitySeconds = 300, from?: string | null, to?: string | null): Observable<StreamChatAnalytics | null> {
      const sanitizedUserId = (userId ?? '').trim();
      if (!sanitizedUserId) {
        return of(null);
      }

      const base = this.getBatchBaseUrl();
      const params = new URLSearchParams({
        userId: sanitizedUserId,
        limit: String(Math.min(Math.max(limit, 1), 20)),
        granularitySeconds: String(Math.max(1, Math.floor(granularitySeconds)))
      });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const url = `${base}/batch/chat-analytics?${params.toString()}`;
    return this.http.get<StreamChatAnalytics>(url, {
      withCredentials: true,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(response => {
        const body = response.body;
        if (!body || !(body as Partial<StreamChatAnalytics>).streamSessionId) {
          return null;
        }
        return body;
      })
    );
  }

  getStreamChatAnalyticsBySessionId(streamSessionId: string, limit = 5, granularitySeconds = 300, from?: string | null, to?: string | null): Observable<StreamChatAnalytics | null> {
    const sanitizedSessionId = (streamSessionId ?? '').trim();
    if (!sanitizedSessionId) {
      return of(null);
    }

    const base = this.getBatchBaseUrl();
    const params = new URLSearchParams({
      streamSessionId: sanitizedSessionId,
      limit: String(Math.min(Math.max(limit, 1), 20)),
      granularitySeconds: String(Math.max(1, Math.floor(granularitySeconds)))
    });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return this.http.get<StreamChatAnalytics>(`${base}/batch/chat-analytics?${params.toString()}`, {
      withCredentials: true,
      observe: 'response',
      context: createSkipGlobalLoaderContext()
    }).pipe(
      map(response => {
        const body = response.body;
        if (!body || !(body as Partial<StreamChatAnalytics>).streamSessionId) {
          return null;
        }
        return body;
      })
    );
  }

  getStreamChatAnalyticsSessionsByUserId(userId: string, limit?: number | null): Observable<StreamSessionSummary[]> {
    const sanitizedUserId = (userId ?? '').trim();
    if (!sanitizedUserId) {
      return of([]);
    }

    const base = this.getBatchBaseUrl();
    const params = new URLSearchParams({
      userId: sanitizedUserId
    });
    if (limit != null) {
      params.set('limit', String(Math.min(Math.max(limit, 1), 500)));
    }
    return this.http.get<StreamSessionSummary[]>(`${base}/batch/chat-analytics/sessions?${params.toString()}`, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    }).pipe(
      catchError(() => of([]))
    );
  }

  refreshStreamChatAnalyticsByUserId(userId: string, maxSessions = 20, limit = 10, granularitySeconds = 300): Observable<StreamChatAnalyticsRefreshResponse> {
    const sanitizedUserId = (userId ?? '').trim();
    if (!sanitizedUserId) {
      return of({ refreshedSessions: 0, streamSessionIds: [] });
    }

    const base = this.getBatchBaseUrl();
    const params = new URLSearchParams({
      userId: sanitizedUserId,
      maxSessions: String(Math.min(Math.max(maxSessions, 1), 100)),
      limit: String(Math.min(Math.max(limit, 1), 50)),
      granularitySeconds: String(Math.max(1, Math.floor(granularitySeconds)))
    });
    return this.http.post<StreamChatAnalyticsRefreshResponse>(`${base}/batch/chat-analytics/refresh?${params.toString()}`, null, {
      withCredentials: true,
      context: createSkipGlobalLoaderContext()
    });
  }

  getStreamChatFaqs(enabledOnly = true): Observable<StreamChatFaqEntry[]> {
    const block = this.requireAuth<StreamChatFaqEntry[]>();
    if (block) return block;
    return this.http.get<StreamChatFaqEntry[]>(`${this.getSecurityBase()}/me/chat-faqs`, {
      withCredentials: true,
      params: { enabledOnly: String(enabledOnly) },
      context: createSkipGlobalLoaderContext()
    }).pipe(
      catchError(() => of([]))
    );
  }

  createStreamChatFaq(body: Omit<StreamChatFaqEntry, 'id' | 'normalizedQuestion' | 'createdAt' | 'updatedAt'>): Observable<StreamChatFaqEntry> {
    const block = this.requireAuth<StreamChatFaqEntry>();
    if (block) return block;
    return this.http.post<StreamChatFaqEntry>(`${this.getSecurityBase()}/me/chat-faqs`, body, {
      withCredentials: true
    });
  }

  updateStreamChatFaq(id: string, body: Omit<StreamChatFaqEntry, 'id' | 'normalizedQuestion' | 'createdAt' | 'updatedAt'>): Observable<StreamChatFaqEntry> {
    const block = this.requireAuth<StreamChatFaqEntry>();
    if (block) return block;
    return this.http.put<StreamChatFaqEntry>(`${this.getSecurityBase()}/me/chat-faqs/${id}`, body, {
      withCredentials: true
    });
  }

  deleteStreamChatFaq(id: string): Observable<void> {
    const block = this.requireAuth<void>();
    if (block) return block;
    return this.http.delete<void>(`${this.getSecurityBase()}/me/chat-faqs/${id}`, {
      withCredentials: true
    });
  }
}
